import React, { useEffect, useRef, useState } from 'react'
import { motion } from 'framer-motion'
import { useLocation } from 'wouter'
import { Button } from '@/components/ui/primitives/button'
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogClose,
} from '@/components/ui/primitives/dialog'
import { Menu } from 'lucide-react'
import { easing, duration, spring } from '@/lib/animations'

const navItems = [
  { href: '/resume', label: 'resume', key: '1' },
  { href: '/photos', label: 'photos', key: '2' },
  { href: '/thoughts', label: 'thoughts', key: '3' },
  { href: '/tools', label: 'tools', key: '4' },
  { href: '/about', label: 'about', key: '5' }
]

function Navigation({ layout = 'vertical', className = '' }) {
  const [location, navigate] = useLocation()
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const isHome = location === '/'
  const navRefs = useRef({})
  const navContainerRef = useRef(null)
  
  // Auto-switch to horizontal on non-homepage pages
  const actualLayout = isHome ? layout : 'horizontal'
  const isHorizontal = actualLayout === 'horizontal'

  // Close mobile menu on route change
  useEffect(() => {
    setMobileMenuOpen(false)
  }, [location])

  useEffect(() => {
    const handleKeyPress = (event) => {
      // Don't handle if mobile menu is open (let it handle its own navigation)
      if (mobileMenuOpen) return
      
      // Handle number keys 1-5
      if (event.key >= '1' && event.key <= '5') {
        const navItem = navItems.find(item => item.key === event.key)
        if (navItem) {
          navigate(navItem.href)
          const linkRef = navRefs.current[navItem.href]
          if (linkRef) {
            linkRef.focus()
          }
        }
      }
    }

    document.addEventListener('keydown', handleKeyPress)
    return () => document.removeEventListener('keydown', handleKeyPress)
  }, [navigate, mobileMenuOpen])

  return (
    <>
      {/* Mobile hamburger - only when horizontal layout */}
      {isHorizontal && (
        <Dialog open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
          <div className="fixed top-4 right-4 z-40 sm:hidden">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setMobileMenuOpen(true)}
              aria-label="Open menu"
            >
              <Menu className="h-5 w-5" />
            </Button>
          </div>

          <DialogContent 
            className="w-[calc(100%-2rem)] max-w-sm rounded-lg border border-border p-6 sm:hidden"
          >
            <DialogTitle className="sr-only">Navigation Menu</DialogTitle>
            
            <nav className="flex flex-col gap-2 mt-4">
              {navItems.map((item) => {
                const isActive = location === item.href || location.startsWith(item.href + '/')
                
                return (
                  <DialogClose asChild key={item.href}>
                    <Button
                      variant={isActive ? "default" : "ghost"}
                      className="justify-start font-mono text-sm"
                      onClick={() => navigate(item.href)}
                    >
                      <span className={isActive ? '' : 'underline'}>{item.label}</span>
                    </Button>
                  </DialogClose>
                )
              })}
            </nav>
          </DialogContent>
        </Dialog>
      )}

      {/* Single nav container - responsive classes handle visibility */}
      <motion.div 
        ref={navContainerRef}
        layout
        className={`flex ${isHorizontal ? 'hidden sm:flex flex-row' : 'flex-col'} ${className}`}
        style={{
          alignItems: 'flex-start',
          gap: '2rem',
          flexShrink: 0,
          willChange: 'transform'
        }}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{
          opacity: {
            duration: duration.fast,
            ease: easing.outQuad
          },
          layout: spring.default
        }}
      >
        {navItems.map((item) => {
          const isActive = location === item.href || location.startsWith(item.href + '/')
          
          return (
            <motion.div 
              key={item.href} 
              layoutId={`nav-item-${item.href}`}
              layout 
              style={{ flexShrink: 0 }}
              transition={{ layout: spring.default }}
            >
              <Button
                ref={(el) => {
                  navRefs.current[item.href] = el
                }}
                variant={isActive ? "default" : "ghost"}
                className={`font-mono text-sm hover-transitions focus-transitions select-none`}
                onClick={() => navigate(item.href)}
              >
                <span className="text-muted-foreground text-xs">[{item.key}]</span>&nbsp;<span className={isActive ? '' : 'underline'}>{item.label}</span>
              </Button>
            </motion.div>
          )
        })}
      </motion.div>
    </>
  )
}

export default Navigation
